import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import AdminLayout from '../../components/AdminLayout';

function exportCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('customers'); // 'customers' | 'abandoned'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/customers?search=${encodeURIComponent(search)}&limit=200`)
      .then(r => { setCustomers(r.data.customers); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/customers/abandoned').then(r => setAbandoned(r.data)).catch(() => {});
  }, [search]);

  const handleExport = () => {
    const rows = customers.map(c => ({
      Name: c.name, Email: c.email,
      'Join Date': c.created_at?.slice(0,10),
      'Total Orders': c.order_count,
      'Total Spent ($)': Number(c.total_spent).toFixed(2),
      'Last Order': c.last_order_at?.slice(0,10) || 'Never',
    }));
    exportCSV(rows, 'styleme-customers.csv');
  };

  const tabStyle = (t) => ({
    padding: '8px 20px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
    borderBottom: tab === t ? '2px solid #4f46e5' : '2px solid transparent',
    color: tab === t ? '#4f46e5' : '#6b7280', background: 'none',
  });

  return (
    <AdminLayout title="Customers">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Customers</h2>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>{total} registered customers</p>
        </div>
        <button className="btn btn-outline" onClick={handleExport}>&#8595; Export CSV</button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 20 }}>
        <button style={tabStyle('customers')} onClick={() => setTab('customers')}>
          All Customers ({total})
        </button>
        <button style={tabStyle('abandoned')} onClick={() => setTab('abandoned')}>
          Abandoned Carts {abandoned.length > 0 && <span style={{ background: '#ef4444', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: 11, marginLeft: 6 }}>{abandoned.length}</span>}
        </button>
      </div>

      {tab === 'customers' && (
        <>
          <input
            style={{ padding: '9px 14px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, width: 300, marginBottom: 16, boxSizing: 'border-box' }}
            placeholder="Search by name or email..."
            value={search} onChange={e => setSearch(e.target.value)}
          />
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  {['Name','Email','Joined','Orders','Total Spent','Last Order'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>}
                {!loading && customers.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No customers found</td></tr>
                )}
                {customers.map(c => (
                  <tr key={c.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{c.name}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#4f46e5' }}>
                      <a href={`mailto:${c.email}`} style={{ color: 'inherit' }}>{c.email}</a>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>{c.created_at?.slice(0,10)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {c.order_count > 0 ? (
                        <Link to={`/admin/orders?customer=${c.id}`} style={{ fontWeight: 600, color: '#4f46e5' }}>{c.order_count}</Link>
                      ) : <span style={{ color: '#9ca3af' }}>0</span>}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: c.total_spent > 0 ? '#10b981' : '#9ca3af' }}>
                      ${Number(c.total_spent).toFixed(2)}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>
                      {c.last_order_at ? c.last_order_at.slice(0,10) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'abandoned' && (
        <div>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
            These customers added items to cart but didn&apos;t complete checkout. Consider sending them a reminder.
          </p>
          {abandoned.length === 0 ? (
            <div className="empty-state"><div className="icon">&#128722;</div><h3>No abandoned carts</h3><p>All customers who added to cart have checked out.</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {abandoned.map(cart => (
                <div key={cart.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{cart.name}</div>
                      <a href={`mailto:${cart.email}`} style={{ fontSize: 13, color: '#4f46e5' }}>{cart.email}</a>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: '#ef4444' }}>${Number(cart.subtotal).toFixed(2)}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>Abandoned {cart.updated_at?.slice(0,10)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {cart.items.map((item, i) => (
                      <span key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
                        {item.product_name} &times;{item.quantity} — ${Number(item.price * item.quantity).toFixed(2)}
                        {item.variant && <span style={{ color: '#6b7280', marginLeft: 4 }}>({Object.values(item.variant).join('/')})</span>}
                      </span>
                    ))}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <a href={`mailto:${cart.email}?subject=You left something in your cart!&body=Hi ${cart.name},%0D%0A%0D%0AYou left $${Number(cart.subtotal).toFixed(2)} worth of items in your StyleMe cart.%0D%0A%0D%0AUse code WELCOME10 for 10%25 off and complete your order:%0D%0Ahttps://styleme.club/cart%0D%0A%0D%0A— The StyleMe Team`}
                      className="btn btn-sm btn-outline" style={{ fontSize: 12 }}>
                      &#9993; Send Reminder Email
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
          {abandoned.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-outline btn-sm" onClick={() => {
                const rows = abandoned.map(c => ({ Name: c.name, Email: c.email, 'Cart Value': Number(c.subtotal).toFixed(2), 'Abandoned At': c.updated_at?.slice(0,10) }));
                exportCSV(rows, 'abandoned-carts.csv');
              }}>&#8595; Export Abandoned Carts CSV</button>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
